// Utility helpers for working with market-prefixed product URLs
function normalizeMarketPrefix(prefix) {
  if (!prefix) {
    return "";
  }

  const trimmed = String(prefix)
    .split("?")[0]
    .split("#")[0]
    .trim();

  if (!trimmed) {
    return "";
  }

  const withLeadingSlash = trimmed.startsWith("/")
    ? trimmed
    : `/${trimmed}`;

  return withLeadingSlash.replace(/\/+$/, "");
}

function extractMarketPrefixFromPath(path) {
  if (!path) {
    return "";
  }

  let target = String(path).trim();
  if (!target) {
    return "";
  }

  if (!target.startsWith("/")) {
    target = `/${target}`;
  }

  const match = target.match(/^(\/[a-z]{2}-[a-z]{2})/i);
  return match ? normalizeMarketPrefix(match[1]) : "";
}

function extractMarketPrefixFromUrl(url) {
  if (!url) {
    return "";
  }

  try {
    const parsed = new URL(url, window.location.origin);
    const pathPrefix = extractMarketPrefixFromPath(parsed.pathname);
    if (pathPrefix) {
      return pathPrefix;
    }

    const marketParam = parsed.searchParams.get("market");
    if (marketParam) {
      return normalizeMarketPrefix(marketParam);
    }

    return "";
  } catch (error) {
    const fallbackPrefix = extractMarketPrefixFromPath(url);
    if (fallbackPrefix) {
      return fallbackPrefix;
    }

    const marketParamMatch = String(url).match(/[?&]market=([a-z]{2}-[a-z]{2})/i);
    if (marketParamMatch) {
      return normalizeMarketPrefix(marketParamMatch[1]);
    }

    return "";
  }
}

function getActiveMarketPrefix() {
  const html = document.documentElement;
  const candidates = [];

  if (html) {
    if (html.dataset?.market) {
      candidates.push(html.dataset.market);
    }
    if (html.dataset?.marketPrefix) {
      candidates.push(html.dataset.marketPrefix);
    }
    if (html.getAttribute("data-market-prefix")) {
      candidates.push(html.getAttribute("data-market-prefix"));
    }
    if (html.getAttribute("lang")) {
      candidates.push(html.getAttribute("lang"));
    }
  }

  const shopifyRoot = window.Shopify?.routes?.root;
  const routesRoot = window.routes?.root;
  const cartAddUrl = window.routes?.cart_add_url;
  const canonicalHref = document
    ?.querySelector('link[rel="canonical"]')
    ?.getAttribute("href");

  candidates.push(
    shopifyRoot,
    routesRoot,
    cartAddUrl,
    canonicalHref,
    window.location?.pathname,
    window.location?.href
  );

  for (const candidate of candidates) {
    const normalized =
      extractMarketPrefixFromPath(candidate) ||
      extractMarketPrefixFromUrl(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "";
}

function ensureCardMarketPrefix(cardElement) {
  if (!cardElement) {
    return;
  }

  const existing = normalizeMarketPrefix(cardElement.dataset?.marketPrefix);
  if (existing) {
    return;
  }

  const prefix = getActiveMarketPrefix();
  if (prefix) {
    cardElement.dataset.marketPrefix = prefix;
  }
}

function buildProductPath(handle, marketPrefix) {
  const sanitizedHandle = String(handle || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+|\/+$/g, "");

  if (!sanitizedHandle) {
    return "";
  }

  const prefix = normalizeMarketPrefix(marketPrefix);
  const basePath = `/products/${sanitizedHandle}`;

  return prefix ? `${prefix}${basePath}` : basePath;
}

function parseProductInfo(rawUrl, fallbackMarketPrefix) {
  let pathname = "";

  if (rawUrl) {
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      pathname = parsed.pathname;
    } catch (error) {
      pathname = rawUrl;
    }
  }

  pathname = String(pathname || "")
    .split("?")[0]
    .split("#")[0];

  if (!pathname.startsWith("/")) {
    pathname = `/${pathname}`;
  }

  const fallbackPrefix =
    extractMarketPrefixFromPath(fallbackMarketPrefix) ||
    extractMarketPrefixFromUrl(fallbackMarketPrefix) ||
    getActiveMarketPrefix();

  let marketPrefix = extractMarketPrefixFromPath(pathname);
  if (marketPrefix) {
    pathname = pathname.slice(marketPrefix.length) || "/";
  }

  if (!marketPrefix) {
    marketPrefix = fallbackPrefix;
  }

  if (!pathname.startsWith("/")) {
    pathname = `/${pathname}`;
  }

  const lowerPath = pathname.toLowerCase();
  const productSegment = "/products/";
  const segmentIndex = lowerPath.indexOf(productSegment);
  let handle = "";

  if (segmentIndex !== -1) {
    handle = pathname.slice(segmentIndex + productSegment.length);
  } else {
    handle = pathname.replace(/^\/+/, "");
  }

  handle = handle
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+|\/+$/g, "");

  const normalizedProductPath = handle
    ? buildProductPath(handle, marketPrefix)
    : "";

  return {
    marketPrefix: normalizeMarketPrefix(marketPrefix),
    handle,
    normalizedProductPath,
  };
}

// Function to fetch product data by handle
window.fetchProductData = function (
  handle,
  cardWrapper,
  badgeValue,
  productId,
  variantId,
  label,
  passedMarketPrefix // Optional market prefix passed from click handler
) {
  // Validate inputs
  if (!handle || !cardWrapper) {
    console.error("Invalid parameters passed to fetchProductData", { handle, cardWrapper });
    return;
  }

  // Prevent duplicate requests by checking if a request is already in progress
  if (cardWrapper.dataset.loading === 'true') {
    return;
  }

  // Mark card as loading
  cardWrapper.dataset.loading = 'true';

  // Preserve current market path (e.g., /en-nz, /en-au, etc.)
  // Use passed market prefix if available, otherwise extract from URL
  const currentPath = window.location.pathname;
  ensureCardMarketPrefix(cardWrapper);
  const storedMarketPrefix = normalizeMarketPrefix(
    cardWrapper?.dataset?.marketPrefix
  );

  let marketPrefix = normalizeMarketPrefix(passedMarketPrefix || storedMarketPrefix);
  const fallbackMarketPrefix =
    marketPrefix ||
    extractMarketPrefixFromPath(currentPath) ||
    extractMarketPrefixFromUrl(window.location.href) ||
    getActiveMarketPrefix();

  const originalHandleInput = handle;
  const handleInfo = parseProductInfo(
    handle && handle.includes("/")
      ? handle
      : `/products/${handle}`,
    fallbackMarketPrefix
  );

  marketPrefix = normalizeMarketPrefix(
    handleInfo.marketPrefix || fallbackMarketPrefix
  );
  handle = handleInfo.handle;

  if (!handle) {
    console.error("Unable to determine product handle", {
      originalHandle: originalHandleInput,
      cardWrapper,
    });
    cardWrapper.dataset.loading = "false";
    return;
  }

  const productPath =
    handleInfo.normalizedProductPath || buildProductPath(handle, marketPrefix);
  if (!productPath) {
    console.error("Unable to build product path", {
      handle,
      marketPrefix,
    });
    cardWrapper.dataset.loading = "false";
    return;
  }

  const productPathWithSlash = productPath.startsWith("/")
    ? productPath
    : `/${productPath}`;
  const productUrlForFetch = `${productPathWithSlash}.js`;

  fetch(productUrlForFetch)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Product not found: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      if (error.name === "SyntaxError" && error.message.includes("JSON")) {
        // Retry the request if JSON parse error
        return fetch(productUrlForFetch).then((response) => response.json());
      }
      throw error;
    })
    .then((productData) => {
      let images = productData.images;
      let comparePrice = productData.variants[0].compare_at_price;
      let price = productData.variants[0].price;
      let title = productData.title;
      let resolvedMarketPrefix = marketPrefix;
      let productUrl = productPathWithSlash;

      // Preserve market prefix in product URL
      if (!resolvedMarketPrefix) {
        // Fallback: try extracting from full URL
        const fullUrl = window.location.href;
        const urlMarketMatch = fullUrl.match(/\/\/([^\/]+)\/([a-z]{2}-[a-z]{2})/i);
        if (urlMarketMatch) {
          resolvedMarketPrefix = `/${urlMarketMatch[2]}`;
          productUrl = buildProductPath(handle, resolvedMarketPrefix);
        }
      }

      if (!resolvedMarketPrefix) {
        resolvedMarketPrefix =
          extractMarketPrefixFromUrl(window.location.href) ||
          getActiveMarketPrefix();
        if (resolvedMarketPrefix) {
          productUrl = buildProductPath(handle, resolvedMarketPrefix);
        }
      }

      if (resolvedMarketPrefix && !productUrl.startsWith(resolvedMarketPrefix)) {
        productUrl = buildProductPath(handle, resolvedMarketPrefix);
      }

      if (cardWrapper) {
        cardWrapper.dataset.marketPrefix =
          normalizeMarketPrefix(resolvedMarketPrefix) || "";
      }
      
      // Ensure productUrl is valid and has the correct format
      if (!productUrl.startsWith('/')) {
        productUrl = '/' + productUrl;
      }
      
      let variants = productData.variants;

      // Find the correct variant based on the variantId passed to the function
      const selectedVariant = productData.variants.find(
        (v) => v.id.toString() === variantId.toString()
      );
      if (selectedVariant) {
        comparePrice = selectedVariant.compare_at_price;
        price = selectedVariant.price;
      }

      // Update carousel images
      const carouselTrack = cardWrapper.querySelector(
        ".card__media-carousel-track"
      );

      if (carouselTrack && images && images.length > 0) {
        carouselTrack.innerHTML = "";

        images.slice(0, 4).forEach((image, index) => {

          const slide = document.createElement("div");
          slide.className = `card__media-slide${index === 0 ? " active" : ""}`;
          slide.setAttribute("data-index", index);

          // Create proper image URLs with Shopify image transformations
          const imageUrl = image.includes("?") ? image.split("?")[0] : image;
          const img165 = `${imageUrl}?width=165`;
          const img360 = `${imageUrl}?width=360`;
          const img533 = `${imageUrl}?width=533`;
          const img720 = `${imageUrl}?width=720`;

          slide.innerHTML = `
            <a href="${productUrl}" class="media media--transparent media--hover-effect">
              <img
                srcset="${img165} 165w, ${img360} 360w, ${img533} 533w, ${img720} 720w"
                src="${img533}"
                sizes="(min-width: ${window.settings?.page_width || 1440}px) ${
            (window.settings?.page_width - 130 || 1310) / 4
          }px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                alt="${title}"
                class="motion-reduce"
                ${index !== 0 ? 'loading="lazy"' : ""}
                width="533"
                height="533"
              >
            </a>
          `;
          carouselTrack.appendChild(slide);
        });

        // Reinitialize carousel
        const carousel = carouselTrack.closest(".card__media-carousel");
        if (carousel) {
          carousel.classList.remove("initialized");
          if (window.ProductCardCarousel) {
            new window.ProductCardCarousel(carousel);
          }
        }
      }

      const variantList = cardWrapper.querySelector("#variant-list");
      if (variantList) {
        variantList.innerHTML = "";
        // Iterate over each variant and create a list item
        productData.variants.forEach((variant) => {
          const listItem = document.createElement("li");
          listItem.innerHTML = `
            <input type="radio" 
                   data-variant-id="${variant.id}" 
                   class="visually-hidden">
            <label class="add-to-cart-button${
              variant.available ? "" : " unavailable"
            }" 
                   data-variant-id="${variant.id}">
                ${variant.title}
            </label>
          `;
          variantList.appendChild(listItem);
        });
      }

      setTimeout(() => {
        // Update price elements
        const priceContainer = cardWrapper.querySelector(".price__container");
        const comparePriceElement = cardWrapper.querySelector(
          ".price-item--regular"
        );
        const salePriceElement = cardWrapper.querySelector(
          ".price-item--sale.price-item--last"
        );

        // Update prices
        if (comparePrice != null && comparePrice > 0) {
          // Show compare price and update both prices
          if (comparePriceElement) {
            comparePriceElement.textContent = `$${(comparePrice / 100).toFixed(
              2
            )}`;
            comparePriceElement.classList.remove("hidden");
          }
          if (salePriceElement) {
            salePriceElement.textContent = `$${(price / 100).toFixed(2)}`;
          }
        } else {
          // No compare price - just show the regular price
          if (comparePriceElement) {
            comparePriceElement.textContent = "";
            comparePriceElement.classList.add("hidden");
          }
          if (salePriceElement) {
            salePriceElement.textContent = `$${(price / 100).toFixed(2)}`;
          }
        }

        // Update the product title and URL
        const headingElement = cardWrapper.querySelector(".card__title a");
        if (headingElement) {
          headingElement.textContent = title;
          headingElement.href = productUrl;
        }

        const mediaLink = cardWrapper.querySelector(".media-link");
        if (mediaLink) {
          mediaLink.href = productUrl;
        }

        const primaryCardLink = cardWrapper.querySelector(
          ".card__media a.card__media-link, .card__media a.media-link"
        );
        if (primaryCardLink) {
          primaryCardLink.href = productUrl;
        }

        // Update the badge content if the element and badge value exist
        const badgeElement = cardWrapper.querySelector(".card__badge span");
        if (badgeElement) {
          badgeElement.textContent = badgeValue;
          if (badgeValue === "") {
            badgeElement.classList.add("hidden");
          } else {
            badgeElement.classList.remove("hidden");
          }
        }

        // Update the label content if the element and label value exist
        const labelElement = cardWrapper.querySelector(".bottom-label-text");
        if (labelElement) {
          labelElement.textContent = label;
          if (label === "") {
            labelElement.classList.add("hidden");
          } else {
            labelElement.classList.remove("hidden");
          }
        }

        // Update the Add to Wishlist button
        const addToWishlistButton = cardWrapper.querySelector(
          ".swym-button.swym-add-to-wishlist-view-product"
        );
        if (addToWishlistButton) {
          addToWishlistButton.setAttribute("data-product-id", productId);
          addToWishlistButton.setAttribute("data-variant-id", variantId);

          const wishlistUrl = `${productUrl}?variant=${variantId}`;
          addToWishlistButton.setAttribute("href", wishlistUrl);

          const currentClass = [...addToWishlistButton.classList].find(
            (className) => className.startsWith("product_")
          );
          if (currentClass) {
            addToWishlistButton.classList.remove(currentClass);
          }
          addToWishlistButton.classList.add(`product_${productId}`);
        }

        // Reset quick-add initialization state
        cardWrapper.classList.remove("quick-add-initialized");

        // Reinitialize quick-add functionality
        if (typeof window.setupQuickAdd === "function") {
          window.setupQuickAdd();
        }

        // Reinitialize cart manager if it exists
        if (window.cartManager) {
          window.cartManager.initialize();
        }
      }, 50); // 50ms delay to allow images to load first
    })
    .catch((error) => {
      console.error("Error fetching product data:", error);
    })
    .finally(() => {
      // Remove loading flag
      cardWrapper.dataset.loading = 'false';
    });
};

// Track recent touches to prevent double-firing on Safari mobile
let lastTouchTime = 0;
const TOUCH_COOLDOWN = 300; // ms

// Function to handle swatch clicks using event delegation
function handleSwatchClick(event) {
  // Prevent double-firing on touch devices
  if (event.type === 'touchend') {
    const now = Date.now();
    if (now - lastTouchTime < TOUCH_COOLDOWN) {
      return;
    }
    lastTouchTime = now;
  } else if (event.type === 'click') {
    // If we just handled a touchend, ignore the subsequent click
    if (Date.now() - lastTouchTime < TOUCH_COOLDOWN) {
      return;
    }
  }

  // Don't interfere with expand/collapse buttons
  if (
    event.target.closest(".swatch-expand-btn") ||
    event.target.closest(".swatch-collapse-btn")
  ) {
    return;
  }

  // Check if the clicked element is a swatch
  const swatch =
    event.target.closest(".card-swatch") ||
    event.target.closest(".swatch-link");

  if (!swatch) return;

  // Only process swatches that have the required data attributes
  const dataUrl = swatch.getAttribute("data-url");

  if (!dataUrl) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  
  // Also stop immediate propagation to prevent any parent handlers
  event.stopImmediatePropagation();

  const cardWrapper = swatch.closest(".product-card-wrapper");
  if (!cardWrapper) {
    return;
  }

  ensureCardMarketPrefix(cardWrapper);

  const cardSwatches = cardWrapper.querySelectorAll(".swatch-container");
  cardSwatches.forEach((sw) => sw.classList.remove("active-swatch"));
  swatch.closest(".swatch-container").classList.add("active-swatch");

  const currentPath = window.location.pathname;
  const originalDataUrl = dataUrl;

  const productInfo = parseProductInfo(originalDataUrl, currentPath);
  let { marketPrefix, handle, normalizedProductPath } = productInfo;

  const existingCardLink = cardWrapper.querySelector(".card__title a");
  const existingLinkPrefix = extractMarketPrefixFromUrl(
    existingCardLink?.getAttribute("href")
  );
  const storedMarketPrefix = normalizeMarketPrefix(
    cardWrapper.dataset?.marketPrefix
  );
  const locationMarketPrefix = extractMarketPrefixFromPath(currentPath) ||
    extractMarketPrefixFromUrl(window.location.href) ||
    getActiveMarketPrefix();

  marketPrefix = normalizeMarketPrefix(
    marketPrefix ||
      storedMarketPrefix ||
      existingLinkPrefix ||
      locationMarketPrefix
  );

  if (!handle) {
    console.error("Could not extract product handle from URL:", originalDataUrl);
    return;
  }

  if (!normalizedProductPath) {
    normalizedProductPath = buildProductPath(handle, marketPrefix);
  }

  if (!normalizedProductPath) {
    console.error("Could not normalize product URL:", originalDataUrl);
    return;
  }

  swatch.setAttribute("data-url", normalizedProductPath);
  if (swatch.tagName === "A") {
    swatch.setAttribute("href", normalizedProductPath);
  } else {
    const swatchAnchor = swatch.querySelector("a.swatch-link, a");
    if (swatchAnchor) {
      swatchAnchor.setAttribute("href", normalizedProductPath);
    }
  }

  if (cardWrapper && marketPrefix) {
    cardWrapper.dataset.marketPrefix = marketPrefix;
  }

  const productId = swatch.getAttribute("data-id");
  const variantId = swatch.getAttribute("data-variant-id");
  const badgeValue = swatch.getAttribute("data-badge");
  const label = swatch.getAttribute("data-label");

  // Validate we have the required data
  if (!productId || !variantId) {
    console.error("Missing required swatch data", { productId, variantId, handle });
    return;
  }

  window.fetchProductData(
    handle,
    cardWrapper,
    badgeValue,
    productId,
    variantId,
    label,
    marketPrefix // Pass the market prefix we extracted
  );
}

// Function to initialize swatches using event delegation
let swatchesInitialized = false;

function initializeSwatches() {
  if (swatchesInitialized) {
    return;
  }

  // Add event delegation listener to document for both click and touchend
  // touchend is needed for Safari mobile compatibility
  // Note: Not using { passive: true } because we need preventDefault() to work
  document.addEventListener("click", handleSwatchClick);
  document.addEventListener("touchend", handleSwatchClick);

  // Mark existing swatches as initialized
  const existingSwatches = document.querySelectorAll(
    ".card-swatch:not(.initialized)"
  );

  existingSwatches.forEach((swatch) => {
    swatch.classList.add("initialized");
    ensureCardMarketPrefix(swatch.closest(".product-card-wrapper"));
  });

  document
    .querySelectorAll(".product-card-wrapper")
    .forEach((card) => ensureCardMarketPrefix(card));

  swatchesInitialized = true;
}

// Legacy function for backward compatibility (now just calls main init)
function initializeSwatchesInContainer(container) {
  // Event delegation handles all swatches, so this is no longer needed
  // but we keep it for compatibility
}

// Make the function globally available for use in card-product.liquid
window.initializeSwatchesInContainer = initializeSwatchesInContainer;

// Initialize swatches on page load
document.addEventListener("DOMContentLoaded", function () {
  initializeSwatches();

  // Set up mutation observer to detect new product cards
  const productGrid = document.getElementById("product-grid");
  if (productGrid) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              (node.classList.contains("grid__item") ||
                node.querySelector(".product-card-wrapper"))
            ) {
              if (node.classList.contains("product-card-wrapper")) {
                ensureCardMarketPrefix(node);
              } else {
                ensureCardMarketPrefix(
                  node.querySelector(".product-card-wrapper")
                );
              }
              // Mark new swatches as initialized
              const newSwatches = node.querySelectorAll(".card-swatch:not(.initialized)");
              newSwatches.forEach((swatch) => {
                swatch.classList.add("initialized");
                ensureCardMarketPrefix(swatch.closest(".product-card-wrapper"));
              });
            }
          });
        }
      });
    });

    observer.observe(productGrid, {
      childList: true,
      subtree: true,
    });
  }
});

// Initialize swatches for newly loaded products
document.addEventListener("products:added", function (event) {
  // Mark any new swatches as initialized
  setTimeout(() => {
    const newSwatches = document.querySelectorAll(
      ".card-swatch:not(.initialized)"
    );
    newSwatches.forEach((swatch) => {
      swatch.classList.add("initialized");
      ensureCardMarketPrefix(swatch.closest(".product-card-wrapper"));
    });
  }, 100);
});
